import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Download } from "lucide-react";
import { company, heroImage } from "../mock";
import { useBrochure } from "./BrochureProvider";
import { useEnquiry } from "./EnquiryProvider";

/**
 * Hero: the proof comes first. Capacity, footprint and tenure sit in the
 * first viewport as large display numerals, and the primary action is the
 * enquiry journey rather than a generic "contact".
 */
const PROOF = [
  { num: "1998", label: "Manufacturing since" },
  { num: "4,80,000", label: "Pieces a month" },
  { num: "3", label: "Factories · Mumbai, Umargam, Tiruppur" },
  { num: "500+", label: "Skilled people on 15 lines" },
];

export default function Hero() {
  const navigate = useNavigate();
  const brochure = useBrochure();
  const enquiry = useEnquiry();

  return (
    <section id="home" className="relative min-h-[92vh] flex items-end overflow-hidden">
      <div className="absolute inset-0">
        <img src={heroImage} alt="K.S.E. Inc. garment factory production floor" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0d1640]/80 via-[#111c4e]/55 to-[#0d1640]" />
      </div>

      <div className="relative z-10 kse-container w-full pt-36 pb-28 lg:pb-32">
        <div className="max-w-4xl">
          <p className="kse-eyebrow kse-eyebrow--light">Garment manufacturer &middot; Mumbai, India</p>
          <h1 className="text-white font-extrabold leading-[0.98] text-[2.6rem] sm:text-6xl lg:text-[5.2rem] max-w-4xl">
            Our Relentless Pursuit <span className="text-blue-300">of Quality.</span>
          </h1>
          <p className="text-white/85 text-lg sm:text-xl max-w-2xl mt-7 leading-relaxed">
            Knits, shirts, kids wear and nightwear for global brands &mdash; designed, sampled, produced and shipped
            from our own floors, on schedule, since 1998.
          </p>

          <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-9">
            <button onClick={() => enquiry?.openEnquiry()} className="kse-btn kse-btn--accent px-7 text-base">
              Start a manufacturing enquiry <ArrowRight size={18} />
            </button>
            <button onClick={() => navigate("/showroom")} className="kse-btn bg-white/10 border border-white/40 text-white hover:bg-white/20 backdrop-blur">
              360&deg; showroom
            </button>
            <button onClick={() => brochure?.openBrochure()} className="kse-btn text-white/85 hover:text-white px-3">
              <Download size={18} /> Brochure PDF
            </button>
          </div>
        </div>

        {/* proof strip */}
        <dl className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-8 border-t border-white/15 pt-8">
          {PROOF.map((p) => (
            <div key={p.label}>
              <dt className="order-2 text-blue-100/70 text-xs sm:text-sm tracking-wide mt-2">{p.label}</dt>
              <dd className="kse-num text-white text-4xl sm:text-5xl lg:text-6xl">{p.num}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}
